import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useDbContext } from "../../../context/DbContext";
import WindowHeader from "../../Layout/WindowHeader/WindowHeader";
import SherComboBox from "../../ComboBoxes/SherComboBox";
import headeLogo from "../../../assets/images/shersoftnavLogo.png";
import { useSelector } from "react-redux";
import { selectActiveLocation } from "../../../app/locationSlice";
import { selectActiveFy } from "../../../app/financialYearSlice";
import { useActiveFyLimits } from "../../../utils/fyDateValidation";

// Safe date parser: handles DD/MMM/YYYY, DD/MM/YYYY, YYYY-MM-DD, ISO etc.
const parseDateToYyyyMmDd = (dateStr, fallback = "") => {
  if (!dateStr) return fallback;
  const cleanStr = String(dateStr).trim().split(/[\sT]+/)[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(cleanStr)) return cleanStr;
  const parts = cleanStr.split(/[-/]/);
  if (parts.length === 3) {
    const day = parseInt(parts[0], 10);
    const year = parseInt(parts[2], 10);
    if (year > 1000) {
      let month = parseInt(parts[1], 10);
      if (isNaN(month)) {
        const abbr = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
        month = abbr.indexOf(parts[1].toLowerCase()) + 1;
      }
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31)
        return `${year}-${String(month).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
    }
  }
  const d = new Date(cleanStr);
  if (!isNaN(d.getTime()))
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
  return fallback;
};
function DayBook({ onClose, openReport, zIndex }) {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { dbCode } = useDbContext();

  const activeLocation = useSelector(selectActiveLocation);
  const activeFy    = useSelector(selectActiveFy);
  const { minDate, maxDate, isDateInFy } = useActiveFyLimits();

  const getCurrentDate = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const today = getCurrentDate();

  const storedLocId = sessionStorage.getItem("loginLocationId") || localStorage.getItem("loginLocationId");
  const storedLocName = sessionStorage.getItem("loginLocation") || localStorage.getItem("loginLocation");

  const [daybookData, setDaybookData] = useState({
    fromDate: today,
    toDate:   today,
    reportType: { value: "DAY BOOK", label: "DAY BOOK" },
    location: activeLocation?.id
      ? { value: String(activeLocation.id), label: activeLocation.name }
      : (storedLocId ? { value: String(storedLocId), label: storedLocName || "SHOP" } : { value: "0", label: "All Locations" }),
  });

  const hasAlertedFyRef = useRef(false);
  useEffect(() => {
    if (activeFy && !hasAlertedFyRef.current) {
      if (!isDateInFy(today)) {
        alert("Current date is outside the active financial year");
        hasAlertedFyRef.current = true;
      }
    }
  }, [activeFy, today, isDateInFy]);

  const set = (key, val) => setDaybookData((prev) => ({ ...prev, [key]: val }));

  const [locationOptions, setLocationOptions] = useState([]);

  useEffect(() => {
    const fetchLocation = async () => {
      try {
        const res = await axios.get(`${apiBaseUrl}/otherregistration/getoptions/${dbCode}`);
        const locations = (res.data || []).filter(
          (rec) => String(rec.Type || rec.type || "").toLowerCase() === "location"
        );
        const mapped = locations.map((rec) => ({
          value: String(rec.Auto ?? rec.auto ?? rec.SlNo ?? rec.slno ?? ""),
          label: rec.Name || rec.name || "",
        }));
        const opts = [{ value: "0", label: "All Locations" }, ...mapped];
        setLocationOptions(opts);

        if (opts.length > 0) {
          const targetId = activeLocation?.id || storedLocId;
          const match = targetId ? opts.find((opt) => Number(opt.value) === Number(targetId)) : null;
          set("location", match || opts[0]);
        }
      } catch (err) {
        console.error("Error fetching location options:", err.message);
      }
    };
    if (dbCode) fetchLocation();
  }, [apiBaseUrl, dbCode, activeLocation]);

  const reportTypeOptions = [
    { value: "DAY BOOK", label: "DAY BOOK" },
  ];

  return (
    <div
      className="absolute inset-0 w-full h-full bg-black/40 flex items-center justify-center pointer-events-auto"
      style={{ zIndex }}
    >
      <div className="bg-white w-[480px] shadow-[0_8px_30px_rgb(0,0,0,0.12)] flex flex-col absolute overflow-hidden rounded-xl border border-slate-200">
        
        {/* ── Header ── */}
        <WindowHeader
          title="Day Book"
          logo={headeLogo}
          onClose={onClose}
          style={{ height: "36px" }}
          className="bg-[#0f56d3] text-white"
          titleClassName="text-xs font-semibold tracking-wide"
          closeBtnClassName="w-6 h-6 rounded-[5px] border-none mr-2 flex items-center justify-center bg-red-600 hover:bg-red-700 text-white transition-colors"
        />

        {/* ── Body ── */}
        <div className="p-4 flex flex-col gap-3 bg-slate-50/50">
          
          {/* Period Row */}
          <div className="grid grid-cols-[130px_1fr] items-center gap-[10px]">
            <label className="text-[11px] font-bold text-slate-700 font-['Inter']">Period</label>
            <div className="grid grid-cols-2 gap-[10px]">
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-slate-400 font-semibold font-['Inter'] whitespace-nowrap">From</span>
                <input
                  type="date"
                  min={minDate}
                  max={maxDate}
                  value={daybookData.fromDate}
                  onChange={(e) => set("fromDate", e.target.value)}
                  onBlur={(e) => {
                    const val = e.target.value;
                    if (val && !isDateInFy(val)) {
                      alert("Date incompatible with financial year");
                    }
                  }}
                  className="w-full h-[28px] px-2 border border-slate-300 rounded text-[11px] font-semibold text-slate-800 font-['Inter'] outline-none bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-400/20 transition-all"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] text-slate-400 font-semibold font-['Inter'] whitespace-nowrap">To</span>
                <input
                  type="date"
                  min={minDate}
                  max={maxDate}
                  value={daybookData.toDate}
                  onChange={(e) => set("toDate", e.target.value)}
                  onBlur={(e) => {
                    const val = e.target.value;
                    if (val && !isDateInFy(val)) {
                      alert("Date incompatible with financial year");
                    }
                  }}
                  className="w-full h-[28px] px-2 border border-slate-300 rounded text-[11px] font-semibold text-slate-800 font-['Inter'] outline-none bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-400/20 transition-all"
                />
              </div>
            </div>
          </div>

          <div className="w-full h-px bg-slate-200 my-1" />
          {/* Location */}
          <div className="grid grid-cols-[130px_1fr] items-center gap-[10px] w-full">
            <label className="text-[11px] font-bold text-slate-700 font-['Inter'] text-left whitespace-nowrap">
              Location
            </label>
            <div className="w-full">
              <SherComboBox
                options={locationOptions}
                value={daybookData.location?.label || ""}
                onSelect={(opt) => set("location", opt)}
                onInputChange={(text) => set("location", { value: text, label: text })}
                onFreeInput={(text) => set("location", { value: text, label: text })}
                placeholder="Select Location..."
                inputClassName="text-[11px] font-['Inter'] font-semibold text-slate-900 border border-slate-300 rounded focus:border-blue-500 focus:ring-1 focus:ring-blue-400/20"
                inputHeight="28px"
                inputWidth="100%"
              />
            </div>
          </div>

          {/* Report Type */}
          <div className="grid grid-cols-[130px_1fr] items-center gap-[10px] w-full">
            <label className="text-[11px] font-bold text-slate-700 font-['Inter'] text-left whitespace-nowrap">
              Report Type
            </label>
            <div className="w-full">
              <SherComboBox
                options={reportTypeOptions}
                value={daybookData.reportType?.label || ""}
                onSelect={(opt) => set("reportType", opt)}
                onInputChange={(text) => set("reportType", { value: text, label: text })}
                onFreeInput={(text) => set("reportType", { value: text, label: text })}
                placeholder="Select Type..."
                inputClassName="text-[11px] font-['Inter'] font-semibold text-slate-900 border border-slate-300 rounded focus:border-blue-500 focus:ring-1 focus:ring-blue-400/20"
                inputHeight="28px"
                inputWidth="100%"
              />
            </div>
          </div>

        </div>

        {/* ── Footer ── */}
        <div className="flex justify-end items-center gap-2 border-t border-slate-200 bg-slate-50 px-4 py-2.5">
          <button
            onClick={() => {
              if (daybookData.fromDate && !isDateInFy(daybookData.fromDate)) {
                alert("From date incompatible with financial year");
                return;
              }
              if (daybookData.toDate && !isDateInFy(daybookData.toDate)) {
                alert("To date incompatible with financial year");
                return;
              }
              openReport(daybookData);
            }}
            className="flex items-center gap-1.5 px-4 h-[30px] rounded-md border border-slate-300 bg-white hover:bg-slate-50 active:scale-[0.98] transition-all cursor-pointer shadow-sm text-[11px] font-bold text-[#0f56d3] font-['Inter']"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#2E7D32" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
            Show
          </button>
          <button
            onClick={onClose}
            className="flex items-center gap-1.5 px-4 h-[30px] rounded-md border border-slate-300 bg-white hover:bg-slate-50 active:scale-[0.98] transition-all cursor-pointer shadow-sm text-[11px] font-bold text-slate-600 font-['Inter']"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#D32F2F" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
            Exit
          </button>
        </div>
      </div>
    </div>
  );
}

export default DayBook;
